import React, { useState, useRef, useEffect } from "react";
import Switch from "react-switch";
import { MdSend, MdUpload } from "react-icons/md";
import { FaRegQuestionCircle } from "react-icons/fa";
import { useToast } from "../common/ToastContext";
import { currentUserHasRole } from "../../services/userService";

const ImportModal = ({ onClose, onImport, importType, multiple = false }) => {
  const capitalizedImportType =
    importType.charAt(0).toUpperCase() + importType.slice(1);
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [fileJsons, setFileJsons] = useState([]);
  const [forced, setForced] = useState(false);
  const [canForce, setCanForce] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const fileInputRef = useRef(null);
  const { showToast } = useToast();

  useEffect(() => {
    const checkForceAccess = async () => {
      try {
        const hasEngineerAccess = await currentUserHasRole("admin");
        setCanForce(hasEngineerAccess);
      } catch (error) {
        setCanForce(false);
      }
    };
    checkForceAccess();
  }, []);

  // Handle file upload button click
  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  // Handle file selection
  const handleFileChange = async (e) => {
    e.preventDefault();

    const fileList = e.target.files
      ? multiple
        ? Array.from(e.target.files)
        : [e.target.files[0]]
      : [];
    if (fileList.length === 0) return;
    setSelectedFiles(fileList);

    try {
      for (const file of fileList) {
        if (file.size > 10 * 1024 * 1024) {
          showToast(`File ${file.name} exceeds the 10MB limit`, "error");
          return;
        }

        const fileReader = new FileReader();
        fileReader.onload = (event) => {
          try {
            let fileContent = event.target.result;
            const jsonData = JSON.parse(fileContent);
            setFileJsons((prevJsons) => [...prevJsons, jsonData]);
          } catch (error) {
            console.error("Error reading file:", error);
            showToast(`Failed to read file ${file.name}`, "error");
          }
        };
        const res = fileReader.readAsText(file);
      }
    } catch (error) {
      console.error("File upload failed:", error);
      showToast("Failed to upload files", "error");
    }

    // Clear the file input
    e.target.value = null;
  };

  const handleMouseEnter = (item) => {
    setHoveredItem(item);
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  const handleClose = () => {
    setSelectedFiles(null);
    setFileJsons([]);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center overflow-y-auto z-[8900]">
      <div className="bg-gray-800 p-6 rounded-lg w-[500px] m-4">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center justify-between">
          <div>Import {capitalizedImportType}</div>
          {canForce && (
            <div className="text-sm flex items-center space-x-1">
              <span
                onMouseEnter={() => handleMouseEnter("forceImport")}
                onMouseLeave={handleMouseLeave}
              >
                <FaRegQuestionCircle
                  className="relative mr-1 text-white-400"
                  size={15}
                />
                {hoveredItem === "forceImport" && (
                  <div className="absolute bg-gray-700 text-white text-xs rounded shadow-lg p-2 w-64 z-10000 -ml-20">
                    Caution: Enabling Force Import will overwrite existing{" "}
                    {importType} content.
                  </div>
                )}
              </span>
              Force Import
              <Switch
                onChange={setForced}
                checked={forced}
                onColor="#4299e1"
                offColor="#718096"
                height={20}
                width={40}
              />
            </div>
          )}
        </h2>
        <div className="mb-4">
          <button
            type="button"
            onClick={handleUploadClick}
            className="p-2 rounded-full hover:bg-gray-700 text-gray-300 hover:text-blue-400"
            title="Attach files"
          >
            <MdUpload size={20} />
          </button>
          <label htmlFor="importFileInput" className="ml-2 text-gray-300">
            {!selectedFiles
              ? `Select ${importType} JSON ...`
              : selectedFiles.map((file) => file.name).join(", ")}
          </label>
          <input
            id="importFileInput"
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
            multiple={multiple}
          />
        </div>
        <div className="flex justify-between w-full">
          <button
            type="submit"
            onClick={handleClose}
            className="ml-4 p-2 rounded-full bg-red-600 text-white hover:bg-red-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={() => onImport(fileJsons, forced)}
            disabled={!selectedFiles}
            className={`p-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 ml-4 ${
              !selectedFiles ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <span className="flex items-center">
              <MdSend /> Import
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportModal;
